import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Building2, Send, CheckCircle2, Globe } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { company, services } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnquiryForm = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({
        title: "Please complete required fields",
        description: "Name, email and phone are required.",
      });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/enquiries`, form);
      setSubmitted(true);
      toast({
        title: "Enquiry received!",
        description: "Thank you \u2014 our friendly team will be in touch soon.",
      });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Please try again or call us on 0448 426 209.",
      });
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: Phone, label: "Call us", value: company.phone, href: `tel:${company.phoneRaw}` },
    { icon: Mail, label: "Email us", value: company.email, href: `mailto:${company.email}` },
    { icon: Globe, label: "Website", value: company.website, href: company.websiteUrl },
    { icon: MapPin, label: "Service area", value: company.address },
    { icon: Building2, label: "ACN", value: company.acn },
  ];

  return (
    <section id="enquire" className="py-20 md:py-28 bg-[#f6f9f3]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2">
            <span className="text-sm font-bold uppercase tracking-widest text-[#5aa83a]">
              Get in touch
            </span>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#123c74] leading-tight">
              Let&rsquo;s talk about your support
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              Whether you&rsquo;re new to the NDIS or looking to switch
              providers, we&rsquo;re here to help. Reach out and we&rsquo;ll
              guide you with warmth and honesty.
            </p>

            <div className="mt-8 space-y-4">
              {contactItems.map((item) => {
                const Icon = item.icon;
                const inner = (
                  <div className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm border border-slate-50 hover:shadow-md transition-shadow">
                    <span className="h-12 w-12 rounded-xl bg-[#eef6e8] flex items-center justify-center shrink-0">
                      <Icon className="h-6 w-6 text-[#5aa83a]" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        {item.label}
                      </p>
                      <p className="font-bold text-[#123c74]">{item.value}</p>
                    </div>
                  </div>
                );
                return item.href ? (
                  <a key={item.label} href={item.href} className="block">
                    {inner}
                  </a>
                ) : (
                  <div key={item.label}>{inner}</div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white rounded-[2rem] shadow-xl p-8 md:p-10 border border-slate-50">
              {submitted ? (
                <div className="text-center py-14">
                  <div className="mx-auto h-20 w-20 rounded-full bg-[#eef6e8] flex items-center justify-center">
                    <CheckCircle2 className="h-11 w-11 text-[#5aa83a]" />
                  </div>
                  <h3 className="mt-6 text-2xl font-extrabold text-[#123c74]">
                    Thank you!
                  </h3>
                  <p className="mt-2 text-slate-600 max-w-sm mx-auto">
                    Your enquiry has been received. Our caring team will get
                    back to you very soon.
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="mt-6 rounded-full border-2 border-[#123c74] text-[#123c74] hover:bg-[#123c74] hover:text-white font-semibold"
                  >
                    Send another enquiry
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="font-semibold text-slate-700">
                        Full name *
                      </Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="Jane Smith"
                        className="h-12 rounded-xl border-slate-200 focus-visible:ring-[#5aa83a]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="font-semibold text-slate-700">
                        Phone *
                      </Label>
                      <Input
                        id="phone"
                        value={form.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="04XX XXX XXX"
                        className="h-12 rounded-xl border-slate-200 focus-visible:ring-[#5aa83a]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-semibold text-slate-700">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="you@example.com"
                      className="h-12 rounded-xl border-slate-200 focus-visible:ring-[#5aa83a]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="font-semibold text-slate-700">
                      Service of interest
                    </Label>
                    <Select
                      value={form.service}
                      onValueChange={(v) => handleChange("service", v)}
                    >
                      <SelectTrigger className="h-12 rounded-xl border-slate-200 focus:ring-[#5aa83a]">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((s) => (
                          <SelectItem key={s.title} value={s.title}>
                            {s.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-semibold text-slate-700">
                      How can we help?
                    </Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      placeholder="Tell us a little about your needs..."
                      rows={4}
                      className="rounded-xl border-slate-200 focus-visible:ring-[#5aa83a] resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-[3.25rem] rounded-full bg-[#5aa83a] hover:bg-[#4b9130] text-white font-bold text-base shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {loading ? "Sending..." : "Send Enquiry"}{" "}
                    {!loading && <Send className="h-4 w-4" />}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnquiryForm;
